import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import colors, { fonts, shadows } from '../theme';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function OrganizationRegisterPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Get plan and billing from URL params
    const planFromUrl = searchParams.get('plan') || 'free';
    const billingFromUrl = searchParams.get('billing') || 'monthly';

    const [formData, setFormData] = useState({
        organizationName: '',
        adminFirstName: '',
        adminLastName: '',
        adminEmail: '',
        adminPassword: '',
        planType: planFromUrl
    });
    // eslint-disable-next-line no-unused-vars
    const [billingPeriod, setBillingPeriod] = useState(billingFromUrl);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const planOptions = [
        {
            value: 'free',
            label: 'Free',
            price: { monthly: 0, annual: 0 },
            description: 'Up to 5 users',
            features: ['Unlimited knowledge entries', 'AI-generated quizzes', 'Basic integrations']
        },
        {
            value: 'per_user',
            label: 'Pro',
            price: { monthly: 8, annual: 80 },
            priceUnit: 'per user',
            description: '6-50 users',
            features: ['Everything in Free', 'Priority support', 'Advanced integrations'],
            popular: true
        },
        {
            value: 'enterprise',
            label: 'Enterprise',
            price: { monthly: 399, annual: 4788 },
            description: 'Unlimited users',
            features: ['Everything in Per-User', 'Dedicated support', 'Custom integrations']
        }
    ];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // Combine first and last name
            const fullName = `${formData.adminFirstName} ${formData.adminLastName}`.trim();

            const response = await axios.post(`${API_URL}/auth/organization/register`, {
                organizationName: formData.organizationName,
                subdomain: formData.organizationName.toLowerCase().replace(/[^a-z0-9]/g, ''),
                adminName: fullName,
                adminEmail: formData.adminEmail,
                adminPassword: formData.adminPassword,
                planType: formData.planType
            });

            if (response.data.success) {
                // Store token and user info
                localStorage.setItem('token', response.data.data.token);
                localStorage.setItem('user', JSON.stringify(response.data.data.user));
                localStorage.setItem('organization', JSON.stringify(response.data.data.organization));

                // Redirect to dashboard
                navigate('/dashboard');
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Registration failed. Please try again.');
            setLoading(false);
        }
    };

    const selectedPlan = planOptions.find(p => p.value === formData.planType) || planOptions[0];
    const price = selectedPlan.price[billingPeriod];

    return (
        <div style={{
            fontFamily: fonts.landing,
            background: colors.landing.bg,
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
        }}>
            <style>{`
                .gradient-text {
                    background: ${colors.gradients.coral};
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }
            `}</style>

            {/* Header */}
            <nav style={{
                padding: '24px 48px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${colors.overlay.coralBorder}`,
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer',
                }}
                onClick={() => navigate('/')}>
                    <img
                        src="/logo192.png"
                        alt="Miblio"
                        style={{
                            width: '40px',
                            height: '40px',
                        }}
                    />
                    <span style={{
                        fontSize: '24px',
                        fontWeight: 800,
                    }} className="gradient-text">
                        miblio
                    </span>
                </div>
                <button onClick={() => navigate('/login')} style={{
                    background: 'transparent',
                    border: 'none',
                    color: colors.landing.text,
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 600,
                }}>
                    Already have an account? <span style={{ color: colors.landing.coral }}>Sign In</span>
                </button>
            </nav>

            {/* Main Content */}
            <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '48px 24px',
            }}>
                <div style={{
                    background: colors.white,
                    borderRadius: '24px',
                    boxShadow: shadows.coralLg,
                    width: '100%',
                    maxWidth: '900px',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    overflow: 'hidden',
                }}>
                    {/* Left Side - Form */}
                    <div style={{ padding: '48px' }}>
                        <h1 style={{
                            fontSize: '32px',
                            fontWeight: 800,
                            marginBottom: '8px',
                            background: colors.gradients.coral,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                        }}>
                            Create Your Account
                        </h1>
                        <p style={{
                            color: colors.gray[600],
                            marginBottom: '32px',
                            fontSize: '16px',
                        }}>
                            Get started with Miblio
                        </p>

                        <form onSubmit={handleRegister}>
                            {/* Organization Name */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    color: colors.gray[700],
                                }}>
                                    Organization Name
                                </label>
                                <input
                                    type="text"
                                    name="organizationName"
                                    value={formData.organizationName}
                                    onChange={handleChange}
                                    required
                                    placeholder="Acme Corporation"
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        border: `2px solid ${colors.gray[200]}`,
                                        borderRadius: '12px',
                                        fontSize: '16px',
                                        fontFamily: fonts.landing,
                                        outline: 'none',
                                        transition: 'border-color 0.2s ease',
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = colors.landing.coral}
                                    onBlur={(e) => e.target.style.borderColor = colors.gray[200]}
                                />
                            </div>

                            {/* Name Fields */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '16px',
                                marginBottom: '20px',
                            }}>
                                <div>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '8px',
                                        fontWeight: 600,
                                        fontSize: '14px',
                                        color: colors.gray[700],
                                    }}>
                                        First Name
                                    </label>
                                    <input
                                        type="text"
                                        name="adminFirstName"
                                        value={formData.adminFirstName}
                                        onChange={handleChange}
                                        required
                                        placeholder="John"
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            border: `2px solid ${colors.gray[200]}`,
                                            borderRadius: '12px',
                                            fontSize: '16px',
                                            fontFamily: fonts.landing,
                                            outline: 'none',
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = colors.landing.coral}
                                        onBlur={(e) => e.target.style.borderColor = colors.gray[200]}
                                    />
                                </div>
                                <div>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '8px',
                                        fontWeight: 600,
                                        fontSize: '14px',
                                        color: colors.gray[700],
                                    }}>
                                        Last Name
                                    </label>
                                    <input
                                        type="text"
                                        name="adminLastName"
                                        value={formData.adminLastName}
                                        onChange={handleChange}
                                        required
                                        placeholder="Doe"
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            border: `2px solid ${colors.gray[200]}`,
                                            borderRadius: '12px',
                                            fontSize: '16px',
                                            fontFamily: fonts.landing,
                                            outline: 'none',
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = colors.landing.coral}
                                        onBlur={(e) => e.target.style.borderColor = colors.gray[200]}
                                    />
                                </div>
                            </div>

                            {/* Email */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    color: colors.gray[700],
                                }}>
                                    Work Email
                                </label>
                                <input
                                    type="email"
                                    name="adminEmail"
                                    value={formData.adminEmail}
                                    onChange={handleChange}
                                    required
                                    placeholder="john@acme.com"
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        border: `2px solid ${colors.gray[200]}`,
                                        borderRadius: '12px',
                                        fontSize: '16px',
                                        fontFamily: fonts.landing,
                                        outline: 'none',
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = colors.landing.coral}
                                    onBlur={(e) => e.target.style.borderColor = colors.gray[200]}
                                />
                            </div>

                            {/* Password */}
                            <div style={{ marginBottom: '24px' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    color: colors.gray[700],
                                }}>
                                    Password
                                </label>
                                <input
                                    type="password"
                                    name="adminPassword"
                                    value={formData.adminPassword}
                                    onChange={handleChange}
                                    required
                                    minLength="8"
                                    placeholder="At least 8 characters"
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        border: `2px solid ${colors.gray[200]}`,
                                        borderRadius: '12px',
                                        fontSize: '16px',
                                        fontFamily: fonts.landing,
                                        outline: 'none',
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = colors.landing.coral}
                                    onBlur={(e) => e.target.style.borderColor = colors.gray[200]}
                                />
                            </div>

                            {error && (
                                <div style={{
                                    padding: '12px 16px',
                                    background: colors.red[50],
                                    border: `2px solid ${colors.red[200]}`,
                                    borderRadius: '12px',
                                    color: colors.red[700],
                                    marginBottom: '24px',
                                    fontSize: '14px',
                                }}>
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                style={{
                                    width: '100%',
                                    padding: '16px',
                                    background: loading ? colors.gray[400] : colors.gradients.coral,
                                    border: 'none',
                                    borderRadius: '12px',
                                    color: colors.white,
                                    fontSize: '18px',
                                    fontWeight: 700,
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    boxShadow: loading ? 'none' : shadows.coralMd,
                                    transition: 'all 0.2s ease',
                                }}
                                onMouseOver={(e) => {
                                    if (!loading) {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = shadows.coralLg;
                                    }
                                }}
                                onMouseOut={(e) => {
                                    if (!loading) {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = shadows.coralMd;
                                    }
                                }}
                            >
                                {loading ? 'Creating your organization...' : 'Create Organization'}
                            </button>
                        </form>

                        <p style={{
                            marginTop: '24px',
                            textAlign: 'center',
                            fontSize: '14px',
                            color: colors.gray[600],
                        }}>
                            By signing up, you agree to our{' '}
                            <button
                                onClick={() => navigate('/terms')}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: colors.landing.coral,
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                }}
                            >
                                Terms
                            </button>
                            {' '}and{' '}
                            <button
                                onClick={() => navigate('/privacy')}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: colors.landing.coral,
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                }}
                            >
                                Privacy Policy
                            </button>
                        </p>
                    </div>

                    {/* Right Side - Plan Summary */}
                    <div style={{
                        background: colors.overlay.coralLight,
                        padding: '48px',
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                        <h2 style={{
                            fontSize: '24px',
                            fontWeight: 700,
                            marginBottom: '8px',
                            color: colors.gray[900],
                        }}>
                            Your Selected Plan
                        </h2>
                        <p style={{
                            fontSize: '14px',
                            color: colors.gray[600],
                            marginBottom: '24px',
                        }}>
                            You can change this anytime
                        </p>

                        {/* Plan Options */}
                        <div style={{ marginBottom: '24px' }}>
                            {planOptions.map((plan) => (
                                <label
                                    key={plan.value}
                                    style={{
                                        display: 'block',
                                        padding: '20px',
                                        marginBottom: '12px',
                                        background: formData.planType === plan.value ? colors.white : colors.white + '80',
                                        border: `3px solid ${formData.planType === plan.value ? colors.landing.coral : 'transparent'}`,
                                        borderRadius: '16px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        position: 'relative',
                                    }}
                                    onMouseOver={(e) => {
                                        if (formData.planType !== plan.value) {
                                            e.currentTarget.style.background = colors.white;
                                        }
                                    }}
                                    onMouseOut={(e) => {
                                        if (formData.planType !== plan.value) {
                                            e.currentTarget.style.background = colors.white + '80';
                                        }
                                    }}
                                >
                                    {plan.popular && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '-12px',
                                            right: '16px',
                                            background: colors.gradients.coral,
                                            color: colors.white,
                                            padding: '4px 12px',
                                            borderRadius: '12px',
                                            fontSize: '11px',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                        }}>
                                            Popular
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                        <input
                                            type="radio"
                                            name="planType"
                                            value={plan.value}
                                            checked={formData.planType === plan.value}
                                            onChange={handleChange}
                                            style={{
                                                marginTop: '4px',
                                                accentColor: colors.landing.coral,
                                            }}
                                        />
                                        <div style={{ flex: 1 }}>
                                            <div style={{
                                                fontSize: '20px',
                                                fontWeight: 700,
                                                marginBottom: '4px',
                                                color: colors.gray[900],
                                            }}>
                                                {plan.label}
                                            </div>
                                            <div style={{
                                                fontSize: '14px',
                                                color: colors.gray[600],
                                                marginBottom: '8px',
                                            }}>
                                                {plan.description}
                                            </div>
                                            <ul style={{
                                                listStyle: 'none',
                                                padding: 0,
                                                margin: 0,
                                                fontSize: '13px',
                                                color: colors.gray[700],
                                            }}>
                                                {plan.features.map((feature, i) => (
                                                    <li key={i} style={{
                                                        marginBottom: '4px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                    }}>
                                                        <span style={{ color: colors.landing.coral }}>✓</span>
                                                        {feature}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>

                        <button
                            onClick={() => navigate('/pricing')}
                            style={{
                                padding: '12px',
                                background: 'transparent',
                                border: `2px solid ${colors.landing.coral}`,
                                borderRadius: '12px',
                                color: colors.landing.coral,
                                fontSize: '14px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                marginBottom: '24px',
                            }}
                        >
                            View All Plans & Pricing
                        </button>

                        {/* Price Summary */}
                        <div style={{
                            marginTop: 'auto',
                            paddingTop: '24px',
                            borderTop: `2px solid ${colors.gray[300]}`,
                        }}>
                            <div style={{ marginBottom: '16px' }}>
                                <div style={{
                                    fontSize: '14px',
                                    color: colors.gray[600],
                                    marginBottom: '4px',
                                }}>
                                    Starting at
                                </div>
                                <div style={{
                                    fontSize: '36px',
                                    fontWeight: 800,
                                    color: colors.gray[900],
                                }}>
                                    ${price}
                                    {selectedPlan.priceUnit && (
                                        <span style={{
                                            fontSize: '16px',
                                            fontWeight: 600,
                                            color: colors.gray[600],
                                        }}>
                                            /{selectedPlan.priceUnit}
                                        </span>
                                    )}
                                </div>
                                <div style={{
                                    fontSize: '14px',
                                    color: colors.gray[600],
                                }}>
                                    {billingPeriod === 'monthly' ? 'per month' : 'per year'}
                                </div>
                            </div>

                            {formData.planType === 'free' && (
                                <div style={{
                                    padding: '12px',
                                    background: colors.white,
                                    borderRadius: '12px',
                                    fontSize: '13px',
                                    color: colors.gray[700],
                                    lineHeight: 1.5,
                                }}>
                                    💡 Start free, upgrade anytime as your team grows
                                </div>
                            )}

                            {formData.planType !== 'free' && (
                                <div style={{
                                    padding: '12px',
                                    background: colors.white,
                                    borderRadius: '12px',
                                    fontSize: '13px',
                                    color: colors.gray[700],
                                    lineHeight: 1.5,
                                }}>
                                    💳 You'll enter payment details after creating your account
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default OrganizationRegisterPage;
